import { FormEvent, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import QRCode from "qrcode";
import {
  Badge,
  Button,
  EmptyState,
  Logo,
  Panel,
  StatCard,
  formatMoney,
  orderTone,
  timeAgo,
} from "@globaldine/ui";
import {
  localize,
  merchantRoles,
  supportedLocales,
  type Locale,
  type MenuItem,
  type MenuTranslationDraft,
  type MerchantRole,
  type MerchantUser,
  type Order,
  type OrderStatus,
  type PrintJob,
  type QueueTicket,
  type Store,
  type TranslationGlossaryEntry,
} from "@globaldine/contracts";
import {
  bestLocale,
  errorLabel,
  localeLabels,
  localeListLabel,
  statusLabel as orderStatusLabel,
  timezoneLabel,
  uiLabel,
  valueLabel,
} from "@globaldine/i18n";
import {
  Bell,
  BookOpenText,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Languages,
  LayoutDashboard,
  ListOrdered,
  LogOut,
  MapPin,
  Plus,
  Printer,
  QrCode,
  ReceiptText,
  RefreshCw,
  Settings,
  ShoppingBag,
  Table2,
  Users,
  WalletCards,
  Wifi,
  XCircle,
} from "lucide-react";
import { merchantTranslator } from "./messages.js";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000";
const TOKEN_KEY = "globaldine.merchant.token";

type Page =
  | "dashboard"
  | "orders"
  | "tables"
  | "queue"
  | "menu"
  | "translations"
  | "staff"
  | "printers"
  | "settings";
type Dashboard = {
  metrics: {
    revenue: number;
    orders: number;
    activeOrders: number;
    waitingParties: number;
    averageRating: number;
  };
  financialsVisible?: boolean;
  activeOrders: Order[];
  queue: QueueTicket[];
  printHealth: { pending: number; failed: number };
};
type AuthResponse = { token: string; user: MerchantUser; stores: Store[] };
type TranslationConfig = {
  configured: boolean;
  provider?: string;
  model?: string;
};

const queueTools: Record<
  Locale,
  { qr: string; screen: string; scanHint: string }
> = {
  vi: {
    qr: "Mã QR lấy số",
    screen: "Mở màn hình gọi số",
    scanHint: "Quét để lấy số tại chỗ hoặc đặt mang đi",
  },
  "zh-Hans": {
    qr: "取号二维码",
    screen: "打开叫号大屏",
    scanHint: "扫码选择堂食取号或外带点餐",
  },
  "zh-Hant": {
    qr: "取號二維碼",
    screen: "開啟叫號大屏",
    scanHint: "掃碼選擇堂食取號或外帶點餐",
  },
  en: {
    qr: "Waitlist QR",
    screen: "Open calling screen",
    scanHint: "Scan for dine-in waitlist or takeaway ordering",
  },
  ko: {
    qr: "대기표 QR",
    screen: "호출 화면 열기",
    scanHint: "매장 대기표 또는 포장 주문을 선택",
  },
  ru: {
    qr: "QR очереди",
    screen: "Открыть экран вызова",
    scanHint: "Сканируйте для очереди или заказа с собой",
  },
  ja: {
    qr: "受付QR",
    screen: "呼出画面を開く",
    scanHint: "店内受付または持ち帰り注文を選択",
  },
  th: {
    qr: "QR รับคิว",
    screen: "เปิดจอเรียกคิว",
    scanHint: "สแกนเพื่อรับคิวหรือสั่งกลับบ้าน",
  },
};
const tableStateCopy: Record<
  Locale,
  {
    title: string;
    available: string;
    reserved: string;
    occupied: string;
    cleaning: string;
    disabled: string;
  }
> = {
  vi: {
    title: "Trạng thái bàn & gọi tự động",
    available: "Trống",
    reserved: "Đã giữ",
    occupied: "Đang dùng",
    cleaning: "Chờ dọn",
    disabled: "Tạm ngưng",
  },
  "zh-Hans": {
    title: "桌台状态与自动叫号",
    available: "空闲中",
    reserved: "已预留",
    occupied: "使用中",
    cleaning: "待清洁",
    disabled: "停用",
  },
  "zh-Hant": {
    title: "桌台狀態與自動叫號",
    available: "空閒中",
    reserved: "已預留",
    occupied: "使用中",
    cleaning: "待清潔",
    disabled: "停用",
  },
  en: {
    title: "Table status & auto calling",
    available: "Available",
    reserved: "Reserved",
    occupied: "Occupied",
    cleaning: "Needs cleaning",
    disabled: "Disabled",
  },
  ko: {
    title: "테이블 상태 및 자동 호출",
    available: "비어 있음",
    reserved: "예약됨",
    occupied: "사용 중",
    cleaning: "청소 대기",
    disabled: "사용 안 함",
  },
  ru: {
    title: "Столы и автовызов",
    available: "Свободен",
    reserved: "Зарезервирован",
    occupied: "Занят",
    cleaning: "Ожидает уборки",
    disabled: "Отключён",
  },
  ja: {
    title: "テーブル状態・自動呼出",
    available: "空席",
    reserved: "確保済み",
    occupied: "使用中",
    cleaning: "清掃待ち",
    disabled: "停止",
  },
  th: {
    title: "สถานะโต๊ะและเรียกคิวอัตโนมัติ",
    available: "ว่าง",
    reserved: "จองแล้ว",
    occupied: "ใช้งาน",
    cleaning: "รอทำความสะอาด",
    disabled: "ปิด",
  },
};
const operationsCopy: Record<
  Locale,
  {
    details: string;
    dishes: string;
    move: string;
    voice: string;
    repeats: string;
    interval: string;
    seconds: string;
    takeaway: string;
  }
> = {
  vi: {
    details: "Chi tiết bàn",
    dishes: "Món đã gọi",
    move: "Chuyển bàn",
    voice: "Phát giọng gọi số",
    repeats: "Số lần phát",
    interval: "Khoảng cách",
    seconds: "giây",
    takeaway: "Cho phép đặt món mang đi từ mã QR xếp hàng",
  },
  "zh-Hans": {
    details: "桌台详情",
    dishes: "客户菜品",
    move: "换桌",
    voice: "叫号语音播报",
    repeats: "播报次数",
    interval: "播报间隔",
    seconds: "秒",
    takeaway: "取号二维码开放外带点餐",
  },
  "zh-Hant": {
    details: "桌台詳情",
    dishes: "顧客菜品",
    move: "換桌",
    voice: "叫號語音播報",
    repeats: "播報次數",
    interval: "播報間隔",
    seconds: "秒",
    takeaway: "取號二維碼開放外帶點餐",
  },
  en: {
    details: "Table details",
    dishes: "Ordered dishes",
    move: "Move table",
    voice: "Queue voice announcement",
    repeats: "Repeats",
    interval: "Interval",
    seconds: "seconds",
    takeaway: "Enable takeaway ordering from waitlist QR",
  },
  ko: {
    details: "테이블 상세",
    dishes: "주문 메뉴",
    move: "테이블 이동",
    voice: "대기번호 음성 안내",
    repeats: "반복 횟수",
    interval: "간격",
    seconds: "초",
    takeaway: "대기표 QR에서 포장 주문 허용",
  },
  ru: {
    details: "Детали стола",
    dishes: "Заказанные блюда",
    move: "Сменить стол",
    voice: "Голосовой вызов",
    repeats: "Повторы",
    interval: "Интервал",
    seconds: "сек.",
    takeaway: "Разрешить заказ с собой через QR очереди",
  },
  ja: {
    details: "テーブル詳細",
    dishes: "注文料理",
    move: "席を移動",
    voice: "番号の音声案内",
    repeats: "繰り返し回数",
    interval: "間隔",
    seconds: "秒",
    takeaway: "受付QRから持ち帰り注文を許可",
  },
  th: {
    details: "รายละเอียดโต๊ะ",
    dishes: "รายการอาหาร",
    move: "ย้ายโต๊ะ",
    voice: "เสียงเรียกคิว",
    repeats: "จำนวนครั้ง",
    interval: "ช่วงเวลา",
    seconds: "วินาที",
    takeaway: "เปิดสั่งกลับบ้านจาก QR คิว",
  },
};

async function request<T>(
  path: string,
  init: RequestInit = {},
  token?: string,
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await response.text();
  const body = text ? JSON.parse(text) : {};
  if (!response.ok)
    throw new Error(
      body.detail
        ? `${body.error}: ${body.detail}`
        : (body.error ?? "REQUEST_FAILED"),
    );
  return body as T;
}

function Login({ onLogin }: { onLogin: (auth: AuthResponse) => void }) {
  const [locale, setLocale] = useState<Locale>(() =>
    bestLocale(navigator.language),
  );
  const [merchantCode, setMerchantCode] = useState(
    () => localStorage.getItem("globaldine.merchant.code") ?? "",
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const t = merchantTranslator(locale);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const auth = await request<AuthResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ merchantCode, username, password }),
      });
      localStorage.setItem(TOKEN_KEY, auth.token);
      localStorage.setItem(
        "globaldine.merchant.code",
        merchantCode.toUpperCase(),
      );
      onLogin(auth);
    } catch (reason) {
      setError(errorLabel((reason as Error).message, locale));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="split" style={{ marginBottom: 30 }}>
          <Logo />
          <select
            className="select"
            value={locale}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            {supportedLocales.map((item) => (
              <option key={item} value={item}>
                {localeLabels[item]}
              </option>
            ))}
          </select>
        </div>
        <h1>{t("loginTitle")}</h1>
        <p className="muted">{t("loginHint")}</p>
        <form className="stack" onSubmit={submit}>
          <label className="field">
            <span>{t("merchantCode")}</span>
            <input
              className="input"
              autoCapitalize="characters"
              autoComplete="organization"
              value={merchantCode}
              onChange={(event) => setMerchantCode(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>{t("username")}</span>
            <input
              className="input"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>{t("password")}</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
            />
          </label>
          {error && <div className="form-error">{error}</div>}
          <Button disabled={busy}>{busy ? t("loggingIn") : t("login")}</Button>
        </form>
      </div>
    </div>
  );
}

export function App() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) ?? "",
  );
  const [user, setUser] = useState<MerchantUser>();
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState("");
  const [page, setPage] = useState<Page>("dashboard");
  const [language, setLanguage] = useState<Locale>("zh-Hans");
  const [dashboard, setDashboard] = useState<Dashboard>();
  const [orders, setOrders] = useState<Order[]>([]);
  const [queue, setQueue] = useState<QueueTicket[]>([]);
  const [printJobs, setPrintJobs] = useState<PrintJob[]>([]);
  const [staff, setStaff] = useState<MerchantUser[]>([]);
  const [translationConfig, setTranslationConfig] =
    useState<TranslationConfig>();
  const [drafts, setDrafts] = useState<MenuTranslationDraft[]>([]);
  const [glossary, setGlossary] = useState<TranslationGlossaryEntry[]>([]);
  const [orderFilter, setOrderFilter] = useState("active");
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(false);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState("");
  const [tableForm, setTableForm] = useState({ name: "", seats: 4 });
  const [qrPreview, setQrPreview] = useState<{
    name: string;
    url: string;
    image: string;
  }>();
  const [editingMenuId, setEditingMenuId] = useState("");
  const [draftOpen, setDraftOpen] = useState<MenuTranslationDraft>();
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [presenceCode, setPresenceCode] = useState<{
    code: string;
    expiresAt: string;
  }>();
  const [translationForm, setTranslationForm] = useState<{
    itemId: string;
    sourceLocale: Locale;
    targetLocales: Locale[];
  }>({ itemId: "", sourceLocale: "vi", targetLocales: ["zh-Hans", "en"] });
  const [employeeForm, setEmployeeForm] = useState<{
    username: string;
    displayName: string;
    password: string;
    preferredLanguage: Locale;
    role: MerchantRole;
    storeIds: string[];
  }>({
    username: "",
    displayName: "",
    password: "",
    preferredLanguage: "vi",
    role: "waiter",
    storeIds: [],
  });
  const [glossaryForm, setGlossaryForm] = useState<{
    sourceLocale: Locale;
    sourceText: string;
    targetLocale: Locale;
    preferred: string;
  }>({
    sourceLocale: "vi",
    sourceText: "",
    targetLocale: "zh-Hans",
    preferred: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
  });
  const [menuForm, setMenuForm] = useState<{
    categoryId: string;
    sourceLocale: Locale;
    name: string;
    kitchenName: string;
    description: string;
    price: number;
    image: string;
    station: MenuItem["station"];
    prepMinutes: number;
    allergens: string;
  }>({
    categoryId: "",
    sourceLocale: "vi",
    name: "",
    kitchenName: "",
    description: "",
    price: 0,
    image: "🍽️",
    station: "hot",
    prepMinutes: 10,
    allergens: "",
  });
  const [storeSettings, setStoreSettings] = useState<{
    languages: Locale[];
    kitchenLanguages: Locale[];
    productionTrigger: Store["productionTrigger"];
    tableBinding: Store["tableBinding"];
    radiusMeters: number;
    queueVoice: NonNullable<Store["queueVoice"]>;
    queueTakeawayEnabled: boolean;
  }>({
    languages: ["vi", "en"],
    kitchenLanguages: ["vi"],
    productionTrigger: "merchant_acceptance",
    tableBinding: "optional",
    radiusMeters: 250,
    queueVoice: { enabled: true, repeats: 3, intervalMs: 1800 },
    queueTakeawayEnabled: true,
  });
  const t = merchantTranslator(language);
  const store = stores.find((candidate) => candidate.id === storeId);
  const can = (permission: MerchantUser["permissions"][number]) =>
    user?.permissions.includes(permission) ?? false;
  const canViewFinancials =
    user?.roles.some((role) =>
      ["owner", "manager", "cashier"].includes(role),
    ) ?? false;

  function signOut() {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(undefined);
    setStores([]);
    setStoreId("");
  }

  useEffect(() => {
    if (!token) return;
    request<{ user: MerchantUser; stores: Store[] }>("/api/auth/me", {}, token)
      .then((result) => {
        setUser(result.user);
        setStores(result.stores);
        setStoreId((current) => current || result.stores[0]?.id || "");
        setLanguage(result.user.preferredLanguage);
      })
      .catch(signOut);
  }, [token]);

  async function refreshCore() {
    if (!token || !storeId) return;
    const [dash, orderList, queueList, jobs] = await Promise.all([
      request<Dashboard>(
        `/api/dashboard/merchant?storeId=${storeId}`,
        {},
        token,
      ),
      request<Order[]>(`/api/orders?storeId=${storeId}`, {}, token),
      request<QueueTicket[]>(
        `/api/queue/tickets?storeId=${storeId}`,
        {},
        token,
      ),
      request<PrintJob[]>(`/api/print-jobs?storeId=${storeId}`, {}, token),
    ]);
    setDashboard(dash);
    setOrders(orderList);
    setQueue(queueList);
    setPrintJobs(jobs);
  }

  async function refreshOptional() {
    if (!token || !storeId || !user) return;
    if (can("manage_staff"))
      setStaff(await request<MerchantUser[]>("/api/merchant/staff", {}, token));
    if (can("use_ai_translation")) {
      const [config, nextDrafts, terms] = await Promise.all([
        request<TranslationConfig>(
          "/api/merchant/translation/config",
          {},
          token,
        ),
        request<MenuTranslationDraft[]>(
          `/api/merchant/stores/${storeId}/translation-drafts`,
          {},
          token,
        ),
        request<TranslationGlossaryEntry[]>(
          `/api/merchant/stores/${storeId}/glossary`,
          {},
          token,
        ),
      ]);
      setTranslationConfig(config);
      setDrafts(nextDrafts);
      setGlossary(terms);
    }
  }

  useEffect(() => {
    if (user && storeId)
      Promise.all([refreshCore(), refreshOptional()]).catch((error) =>
        error.message === "AUTH_REQUIRED"
          ? signOut()
          : setToast(errorLabel(error.message, language)),
      );
  }, [user?.id, storeId]);
  useEffect(() => {
    if (!token || !storeId) return;
    const socket = io(API_URL, { auth: { token } });
    socket.emit("subscribe", [`store:${storeId}`]);
    const onOrder = (next: Order) => {
      setOrders((current) => [
        next,
        ...current.filter((order) => order.id !== next.id),
      ]);
      void refreshCore();
      setToast(`${next.pickupNumber ?? next.displayNumber} · ${t("realtime")}`);
    };
    socket.on("order:new", onOrder);
    socket.on("order:update", onOrder);
    socket.on("queue:update", setQueue);
    socket.on("print:new", refreshCore);
    socket.on("menu:update", (menu: MenuItem[]) =>
      setStores((current) =>
        current.map((item) => (item.id === storeId ? { ...item, menu } : item)),
      ),
    );
    return () => {
      socket.close();
    };
  }, [token, storeId, language]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (store)
      setStoreSettings({
        languages: store.languages,
        kitchenLanguages: store.kitchenLanguages,
        productionTrigger: store.productionTrigger,
        tableBinding: store.tableBinding,
        radiusMeters: store.location.radiusMeters,
        queueVoice: store.queueVoice ?? {
          enabled: true,
          repeats: 3,
          intervalMs: 1800,
        },
        queueTakeawayEnabled: store.queueTakeawayEnabled ?? true,
      });
  }, [store?.id]);
  useEffect(() => {
    if (!token || !storeId || !user?.permissions.includes("view_dashboard"))
      return;
    const refresh = () =>
      request<{ code: string; expiresAt: string }>(
        `/api/merchant/presence-code?storeId=${storeId}`,
        {},
        token,
      ).then(setPresenceCode);
    void refresh();
    const timer = setInterval(refresh, 10_000);
    return () => clearInterval(timer);
  }, [token, storeId, user?.id]);

  const filteredOrders = useMemo(
    () =>
      orders.filter((order) =>
        orderFilter === "active"
          ? !["completed", "cancelled"].includes(order.status)
          : order.status === orderFilter,
      ),
    [orders, orderFilter],
  );

  async function changeLanguage(next: Locale) {
    setLanguage(next);
    if (token) {
      const updated = await request<MerchantUser>(
        "/api/auth/me",
        { method: "PATCH", body: JSON.stringify({ preferredLanguage: next }) },
        token,
      );
      setUser(updated);
    }
  }

  async function mutateOrder(
    order: Order,
    action: "accept" | "confirm-payment" | "status",
    status?: OrderStatus,
  ) {
    try {
      const path =
        action === "status"
          ? `/api/orders/${order.id}/status`
          : `/api/orders/${order.id}/${action}`;
      await request<Order>(
        path,
        { method: "POST", body: JSON.stringify({ status }) },
        token,
      );
      await refreshCore();
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    }
  }

  async function toggleMenu(item: MenuItem) {
    if (!store) return;
    await request(
      `/api/stores/${store.id}/menu/${item.id}`,
      { method: "PATCH", body: JSON.stringify({ available: !item.available }) },
      token,
    );
    await refreshCore();
  }

  function openMenuEditor(item?: MenuItem) {
    if (!store) return;
    setEditingMenuId(item?.id ?? "");
    setMenuForm({
      categoryId: item?.categoryId ?? store.categories[0]?.id ?? "",
      sourceLocale: language,
      name: item ? localize(item.name, language) : "",
      kitchenName: item ? localize(item.kitchenName, language) : "",
      description: item ? localize(item.description, language) : "",
      price: item?.price ?? 0,
      image: item?.image ?? "🍽️",
      station: item?.station ?? "hot",
      prepMinutes: item?.prepMinutes ?? 10,
      allergens: item?.allergens.join(", ") ?? "",
    });
    setMenuOpen(true);
  }

  async function saveMenuItem(event: FormEvent) {
    event.preventDefault();
    if (!store) return;
    setLoading(true);
    const payload = {
      ...menuForm,
      allergens: menuForm.allergens
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      dietary: [],
    };
    try {
      const path = editingMenuId
        ? `/api/stores/${store.id}/menu/${editingMenuId}`
        : `/api/stores/${store.id}/menu`;
      const updated = await request<MenuItem>(
        path,
        {
          method: editingMenuId ? "PATCH" : "POST",
          body: JSON.stringify(payload),
        },
        token,
      );
      setStores((current) =>
        current.map((candidate) =>
          candidate.id === store.id
            ? {
                ...candidate,
                menu: editingMenuId
                  ? candidate.menu.map((item) =>
                      item.id === updated.id ? updated : item,
                    )
                  : [...candidate.menu, updated],
              }
            : candidate,
        ),
      );
      setMenuOpen(false);
      setEditingMenuId("");
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    } finally {
      setLoading(false);
    }
  }

  async function queueStatus(
    ticket: QueueTicket,
    status: QueueTicket["status"],
  ) {
    await request(
      `/api/queue/tickets/${ticket.id}/status`,
      { method: "POST", body: JSON.stringify({ status }) },
      token,
    );
    await refreshCore();
  }

  async function createEmployee(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    try {
      await request(
        "/api/merchant/staff",
        {
          method: "POST",
          body: JSON.stringify({
            ...employeeForm,
            roles: [employeeForm.role],
            storeIds: employeeForm.storeIds.length
              ? employeeForm.storeIds
              : [storeId],
          }),
        },
        token,
      );
      setEmployeeOpen(false);
      setEmployeeForm({
        username: "",
        displayName: "",
        password: "",
        preferredLanguage: "vi",
        role: "waiter",
        storeIds: [storeId],
      });
      await refreshOptional();
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    } finally {
      setLoading(false);
    }
  }

  async function updateEmployee(
    account: MerchantUser,
    patch: Record<string, unknown>,
  ) {
    try {
      await request(
        `/api/merchant/staff/${account.id}`,
        { method: "PATCH", body: JSON.stringify(patch) },
        token,
      );
      await refreshOptional();
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    }
  }

  async function resetEmployeePassword(account: MerchantUser) {
    const result = await request<{ temporaryPassword: string }>(
      `/api/merchant/staff/${account.id}/reset-password`,
      { method: "POST", body: "{}" },
      token,
    );
    setTemporaryPassword(result.temporaryPassword);
    await refreshOptional();
  }

  async function generateTranslations() {
    if (!store || !translationForm.itemId) return;
    setLoading(true);
    try {
      await request(
        `/api/merchant/stores/${store.id}/menu/${translationForm.itemId}/translations`,
        {
          method: "POST",
          body: JSON.stringify({
            sourceLocale: translationForm.sourceLocale,
            targetLocales: translationForm.targetLocales,
          }),
        },
        token,
      );
      await refreshOptional();
      setToast(t("drafts"));
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    } finally {
      setLoading(false);
    }
  }

  async function saveDraft(draft: MenuTranslationDraft) {
    const updated = await request<MenuTranslationDraft>(
      `/api/merchant/translation-drafts/${draft.id}`,
      { method: "PATCH", body: JSON.stringify({ fields: draft.fields }) },
      token,
    );
    setDraftOpen(updated);
    await refreshOptional();
  }

  async function publishDraft(draft: MenuTranslationDraft) {
    await saveDraft(draft);
    await request(
      `/api/merchant/translation-drafts/${draft.id}/publish`,
      { method: "POST", body: "{}" },
      token,
    );
    setDraftOpen(undefined);
    await Promise.all([refreshOptional(), refreshCore()]);
  }

  async function addGlossary(event: FormEvent) {
    event.preventDefault();
    await request(
      `/api/merchant/stores/${storeId}/glossary`,
      {
        method: "POST",
        body: JSON.stringify({
          sourceLocale: glossaryForm.sourceLocale,
          sourceText: glossaryForm.sourceText,
          translations: { [glossaryForm.targetLocale]: glossaryForm.preferred },
        }),
      },
      token,
    );
    setGlossaryForm((current) => ({
      ...current,
      sourceText: "",
      preferred: "",
    }));
    await refreshOptional();
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    try {
      const result = await request<{ token: string; user: MerchantUser }>(
        "/api/auth/change-password",
        { method: "POST", body: JSON.stringify(passwordForm) },
        token,
      );
      localStorage.setItem(TOKEN_KEY, result.token);
      setToken(result.token);
      setUser(result.user);
      setPasswordForm({ currentPassword: "", newPassword: "" });
      setToast(t("save"));
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    }
  }

  async function saveStoreSettings() {
    if (!store) return;
    try {
      const updated = await request<Store>(
        `/api/merchant/stores/${store.id}/settings`,
        { method: "PATCH", body: JSON.stringify(storeSettings) },
        token,
      );
      setStores((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setToast(t("save"));
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    }
  }

  async function createTable(event: FormEvent) {
    event.preventDefault();
    if (!store) return;
    setLoading(true);
    try {
      const table = await request<Store["tables"][number]>(
        `/api/stores/${store.id}/tables`,
        { method: "POST", body: JSON.stringify(tableForm) },
        token,
      );
      setStores((current) =>
        current.map((item) =>
          item.id === store.id
            ? { ...item, tables: [...(item.tables ?? []), table] }
            : item,
        ),
      );
      setTableOpen(false);
      setTableForm({ name: "", seats: 4 });
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    } finally {
      setLoading(false);
    }
  }

  async function showTableQr(table: Store["tables"][number]) {
    if (!store) return;
    const url = `${API_URL}/q/${store.slug}/table-${encodeURIComponent(table.id)}`;
    setQrPreview({
      name: table.name,
      url,
      image: await QRCode.toDataURL(url, { width: 420, margin: 2 }),
    });
  }

  async function showQueueQr() {
    if (!store) return;
    const url = `${API_URL}/q/${store.slug}/queue`;
    setQrPreview({
      name: queueTools[language].qr,
      url,
      image: await QRCode.toDataURL(url, { width: 520, margin: 2 }),
    });
  }

  async function setTableStatus(
    table: Store["tables"][number],
    status: "available" | "occupied" | "cleaning" | "disabled",
  ) {
    if (!store) return;
    const updated = await request<Store["tables"][number]>(
      `/api/stores/${store.id}/tables/${table.id}/status`,
      { method: "PATCH", body: JSON.stringify({ status }) },
      token,
    );
    setStores((current) =>
      current.map((item) =>
        item.id === store.id
          ? {
              ...item,
              tables: item.tables.map((candidate) =>
                candidate.id === updated.id ? updated : candidate,
              ),
            }
          : item,
      ),
    );
    await refreshCore();
  }

  async function moveTable(order: Order, targetTableId: string) {
    if (!targetTableId) return;
    try {
      await request(
        `/api/orders/${order.id}/move-table`,
        { method: "POST", body: JSON.stringify({ targetTableId }) },
        token,
      );
      setStores((current) =>
        current.map((item) =>
          item.id !== order.storeId
            ? item
            : {
                ...item,
                tables: item.tables.map((table) => ({
                  ...table,
                  status:
                    table.id === targetTableId
                      ? "occupied"
                      : table.id === order.tableId
                        ? "cleaning"
                        : table.status,
                })),
              },
        ),
      );
      setSelectedTableId(targetTableId);
      await refreshCore();
    } catch (error) {
      setToast(errorLabel((error as Error).message, language));
    }
  }

  if (!token || !user)
    return (
      <Login
        onLogin={(auth) => {
          setToken(auth.token);
          setUser(auth.user);
          setStores(auth.stores);
          setStoreId(auth.stores[0]?.id ?? "");
          setLanguage(auth.user.preferredLanguage);
        }}
      />
    );

  const nav = [
    {
      id: "dashboard" as Page,
      icon: LayoutDashboard,
      permission: "view_dashboard",
    },
    {
      id: "orders" as Page,
      icon: ReceiptText,
      permission: "manage_orders",
      count: orders.filter((order) => order.status === "awaiting_confirmation")
        .length,
    },
    { id: "tables" as Page, icon: Table2, permission: "manage_orders" },
    {
      id: "queue" as Page,
      icon: ListOrdered,
      permission: "manage_queue",
      count: queue.filter((item) => item.status === "waiting").length,
    },
    { id: "menu" as Page, icon: BookOpenText, permission: "manage_menu" },
    {
      id: "translations" as Page,
      icon: Languages,
      permission: "use_ai_translation",
    },
    { id: "staff" as Page, icon: Users, permission: "manage_staff" },
    {
      id: "printers" as Page,
      icon: Printer,
      permission: "manage_printers",
      count: printJobs.filter((job) => job.status === "failed").length,
    },
    { id: "settings" as Page, icon: Settings, permission: "manage_settings" },
  ].filter((item) =>
    can(item.permission as MerchantUser["permissions"][number]),
  );

  const OrderActions = ({ order }: { order: Order }) => (
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
      {order.status === "awaiting_confirmation" &&
        (order.cashierApprovalRequired ||
          store?.productionTrigger === "cashier_confirmation") && (
          <Button
            className="small"
            onClick={() => mutateOrder(order, "confirm-payment")}
          >
            <WalletCards size={14} />
            {t("confirmPayment")}
          </Button>
        )}
      {order.status === "awaiting_confirmation" &&
        !order.cashierApprovalRequired &&
        store?.productionTrigger !== "cashier_confirmation" && (
          <Button
            className="small"
            onClick={() => mutateOrder(order, "accept")}
          >
            <Check size={14} />
            {t("accept")}
          </Button>
        )}
      {order.status === "confirmed" && (
        <Button
          className="small"
          onClick={() => mutateOrder(order, "status", "preparing")}
        >
          {t("startPreparing")}
        </Button>
      )}
      {order.status === "preparing" && (
        <Button
          className="small"
          onClick={() => mutateOrder(order, "status", "ready")}
        >
          {t("markReady")}
        </Button>
      )}
      {order.status === "ready" && (
        <Button
          className="small"
          onClick={() =>
            mutateOrder(
              order,
              "status",
              order.serviceMode === "self_pickup" ? "completed" : "served",
            )
          }
        >
          {order.serviceMode === "self_pickup" ? t("closeOrder") : t("serve")}
        </Button>
      )}
      {order.status === "served" && (
        <Button
          className="small"
          onClick={() => mutateOrder(order, "status", "completed")}
        >
          {t("closeOrder")}
        </Button>
      )}
    </div>
  );

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Logo />
        <nav className="nav-list">
          {nav.map(({ id, icon: Icon, count }) => (
            <button
              key={id}
              className={`nav-item ${page === id ? "active" : ""}`}
              onClick={() => setPage(id)}
            >
              <Icon size={18} />
              <span>{t(id)}</span>
              {count ? <span className="nav-count">{count}</span> : null}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="split">
            <span>
              <Wifi size={13} /> {user.displayName}
            </span>
            <Badge tone="green">
              {user.roles.map((role) => valueLabel(role, language)).join(" / ")}
            </Badge>
          </div>
          <p style={{ margin: "10px 0 0" }}>GlobeDine · v1.0.12</p>
        </div>
      </aside>
      <div className="main-area">
        <header className="topbar">
          <div className="topbar-title">
            <h1>{t(page)}</h1>
            <p>
              {store ? localize(store.name, language) : "—"} ·{" "}
              {store ? timezoneLabel(store.timezone, language) : "—"}
            </p>
          </div>
          <div className="topbar-actions">
            <select
              className="select"
              value={language}
              onChange={(event) =>
                void changeLanguage(event.target.value as Locale)
              }
            >
              {supportedLocales.map((item) => (
                <option key={item} value={item}>
                  {localeLabels[item]}
                </option>
              ))}
            </select>
            <button
              className="icon-button"
              onClick={() => Promise.all([refreshCore(), refreshOptional()])}
            >
              <RefreshCw size={17} />
            </button>
            <button className="icon-button">
              <Bell size={17} />
              <span className="dot" />
            </button>
            <button
              className="icon-button"
              onClick={signOut}
              title={t("logout")}
            >
              <LogOut size={17} />
            </button>
          </div>
        </header>
        <main className="content">
          {user.mustChangePassword && (
            <div className="security-banner">
              {t("temporaryPassword")} · {t("changePassword")}
            </div>
          )}
          {presenceCode && (
            <div className="presence-code-banner">
              <span>
                {language === "zh-Hans"
                  ? "动态到店码"
                  : language === "zh-Hant"
                    ? "動態到店碼"
                    : language === "vi"
                      ? "Mã xác nhận tại quán"
                      : "Dynamic venue code"}
              </span>
              <strong>{presenceCode.code}</strong>
              <small>
                {new Date(presenceCode.expiresAt).toLocaleTimeString(language, {
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </small>
            </div>
          )}
          {page === "tables" && store && (
            <Panel title={tableStateCopy[language].title}>
              <div className="panel-body table-status-list">
                {store.tables.map((table) => (
                  <div className="split" key={`status-${table.id}`}>
                    <span>
                      <b>{table.name}</b>
                      <small className="muted">
                        {" "}
                        · {table.seats} {t("party")}
                      </small>
                    </span>
                    <div className="row-actions">
                      <Badge
                        tone={
                          (table.status ?? "available") === "available"
                            ? "green"
                            : table.status === "disabled"
                              ? "red"
                              : "amber"
                        }
                      >
                        {tableStateCopy[language][table.status ?? "available"]}
                      </Badge>
                      <Button
                        className="small"
                        variant="secondary"
                        onClick={() => void setTableStatus(table, "available")}
                      >
                        {tableStateCopy[language].available}
                      </Button>
                      <Button
                        className="small"
                        variant="secondary"
                        onClick={() => void setTableStatus(table, "occupied")}
                      >
                        {tableStateCopy[language].occupied}
                      </Button>
                      <Button
                        className="small"
                        variant="secondary"
                        onClick={() => void setTableStatus(table, "disabled")}
                      >
                        {tableStateCopy[language].disabled}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {page === "queue" && (
            <div
              className="content-head"
              style={{ justifyContent: "flex-end" }}
            >
              <div className="row-actions">
                <Button variant="secondary" onClick={() => void showQueueQr()}>
                  <QrCode size={16} />
                  {queueTools[language].qr}
                </Button>
                <Button
                  onClick={() =>
                    store &&
                    window.open(
                      `${API_URL}/q/${store.slug}/queue-display`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <ListOrdered size={16} />
                  {queueTools[language].screen}
                </Button>
              </div>
            </div>
          )}

          {page === "dashboard" && dashboard && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("hello")}</h1>
                  <p>{t("subtitle")}</p>
                </div>
                <Badge tone="green">
                  <Wifi size={12} /> {t("realtime")}
                </Badge>
              </div>
              <div className="stats-grid">
                {dashboard.financialsVisible !== false && (
                  <StatCard
                    label={t("revenue")}
                    value={formatMoney(
                      dashboard.metrics.revenue,
                      store?.baseCurrency ?? "VND",
                      language,
                    )}
                    icon={<CircleDollarSign />}
                  />
                )}
                <StatCard
                  label={t("totalOrders")}
                  value={dashboard.metrics.orders}
                  icon={<ShoppingBag />}
                />
                <StatCard
                  label={t("activeOrders")}
                  value={dashboard.metrics.activeOrders}
                  icon={<Clock3 />}
                />
                <StatCard
                  label={t("waitingParties")}
                  value={dashboard.metrics.waitingParties}
                  detail={`★ ${dashboard.metrics.averageRating.toFixed(1)}`}
                  icon={<Users />}
                />
              </div>
              <Panel
                title={t("liveOrders")}
                action={
                  <Button
                    variant="ghost"
                    className="small"
                    onClick={() => setPage("orders")}
                  >
                    {t("orders")} <ChevronRight size={14} />
                  </Button>
                }
              >
                <div className="order-list">
                  {dashboard.activeOrders.map((order) => (
                    <div className="order-row" key={order.id}>
                      <div>
                        <div className="order-number">
                          {order.pickupNumber ?? order.displayNumber}
                        </div>
                        <span className="tiny muted">
                          {order.tableId ??
                            valueLabel(order.fulfillmentMode, language)}
                        </span>
                      </div>
                      <div>
                        <h4>
                          {order.items
                            .map(
                              (item) =>
                                `${item.quantity}× ${localize(item.nameSnapshot, language)}`,
                            )
                            .join(" · ")}
                        </h4>
                        <p>
                          {timeAgo(order.createdAt, language)}
                          {dashboard.financialsVisible !== false
                            ? ` · ${formatMoney(order.total, order.currency, language)}`
                            : ""}
                        </p>
                      </div>
                      <Badge tone={orderTone(order.status)}>
                        {orderStatusLabel(order.status, language)}
                      </Badge>
                      {can("manage_orders") && <OrderActions order={order} />}
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}

          {page === "orders" && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("orders")}</h1>
                  <p>{t("subtitle")}</p>
                </div>
              </div>
              <div className="tabs" style={{ marginBottom: 16 }}>
                {[
                  "active",
                  "awaiting_confirmation",
                  "preparing",
                  "ready",
                  "completed",
                ].map((filter) => (
                  <button
                    key={filter}
                    className={orderFilter === filter ? "active" : ""}
                    onClick={() => setOrderFilter(filter)}
                  >
                    {filter === "active"
                      ? t("activeOrders")
                      : orderStatusLabel(filter, language)}
                  </button>
                ))}
              </div>
              <Panel>
                {filteredOrders.length === 0 ? (
                  <EmptyState icon={<ReceiptText />} title={t("noOrders")} />
                ) : (
                  <div className="order-list">
                    {filteredOrders.map((order) => (
                      <div className="order-row" key={order.id}>
                        <div>
                          <div className="order-number">
                            {order.pickupNumber ?? order.displayNumber}
                          </div>
                          <span className="tiny muted">
                            {order.tableId ??
                              valueLabel(order.fulfillmentMode, language)}
                          </span>
                        </div>
                        <div>
                          <h4>
                            {order.items
                              .map(
                                (item) =>
                                  `${item.quantity}× ${localize(item.nameSnapshot, language)}`,
                              )
                              .join(" · ")}
                          </h4>
                          <p>
                            {orderStatusLabel(order.paymentStatus, language)}
                            {canViewFinancials
                              ? ` · ${formatMoney(order.total, order.currency, language)}`
                              : ""}{" "}
                            · {timeAgo(order.createdAt, language)}
                          </p>
                        </div>
                        <Badge tone={orderTone(order.status)}>
                          {orderStatusLabel(order.status, language)}
                        </Badge>
                        <OrderActions order={order} />
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </>
          )}

          {page === "tables" && store && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("tables")}</h1>
                  <p>{t("realtime")}</p>
                </div>
                <Button onClick={() => setTableOpen(true)}>
                  <Plus size={16} />
                  {t("create")}
                </Button>
              </div>
              <div className="table-grid">
                {(store.tables ?? []).map((table) => {
                  const order = orders.find(
                    (item) =>
                      item.tableId === table.id &&
                      !["completed", "cancelled"].includes(item.status),
                  );
                  return (
                    <article className="panel table-card" key={table.id}>
                      <div className="split">
                        <strong>{table.name}</strong>
                        <Badge tone={order ? orderTone(order.status) : "green"}>
                          {order
                            ? orderStatusLabel(order.status, language)
                            : tableStateCopy[language][
                                table.status ?? "available"
                              ]}
                        </Badge>
                      </div>
                      <p className="muted tiny">{table.seats}</p>
                      {order && (
                        <>
                          <p className="muted tiny">
                            {order.displayNumber} · {order.items.length}
                          </p>
                          {canViewFinancials && (
                            <b>
                              {formatMoney(
                                order.total,
                                order.currency,
                                language,
                              )}
                            </b>
                          )}
                          <Button
                            className="small"
                            onClick={() => setSelectedTableId(table.id)}
                          >
                            {operationsCopy[language].details}
                          </Button>
                        </>
                      )}
                      <Button
                        variant="secondary"
                        className="small"
                        onClick={() => void showTableQr(table)}
                      >
                        <QrCode size={14} /> QR
                      </Button>
                    </article>
                  );
                })}
              </div>
            </>
          )}

          {page === "queue" && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("queue")}</h1>
                  <p>
                    {t("queue")} Q · {t("orders")} P
                  </p>
                </div>
              </div>
              <Panel>
                {queue.length === 0 ? (
                  <EmptyState icon={<ListOrdered />} title={t("noQueue")} />
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t("party")}</th>
                        <th>{t("wait")}</th>
                        <th>{t("source")}</th>
                        <th>{t("status")}</th>
                        <th>{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queue.map((ticket) => (
                        <tr key={ticket.id}>
                          <td>
                            <b>{ticket.number}</b>
                          </td>
                          <td>{ticket.partySize}</td>
                          <td>{ticket.estimatedMinutes} min</td>
                          <td>{valueLabel(ticket.source, language)}</td>
                          <td>
                            <Badge tone={orderTone(ticket.status)}>
                              {orderStatusLabel(ticket.status, language)}
                            </Badge>
                          </td>
                          <td>
                            {ticket.status === "waiting" && (
                              <Button
                                className="small"
                                onClick={() => queueStatus(ticket, "called")}
                              >
                                {t("call")}
                              </Button>
                            )}
                            {ticket.status === "called" && (
                              <>
                                <Button
                                  className="small"
                                  onClick={() => queueStatus(ticket, "seated")}
                                >
                                  {t("seat")}
                                </Button>{" "}
                                <Button
                                  variant="secondary"
                                  className="small"
                                  onClick={() => queueStatus(ticket, "skipped")}
                                >
                                  {t("skip")}
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>
            </>
          )}

          {page === "menu" && store && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("menu")}</h1>
                  <p>{t("menuSynced")}</p>
                </div>
                <Button onClick={() => openMenuEditor()}>
                  <Plus size={16} />
                  {t("create")} {t("dish")}
                </Button>
              </div>
              <Panel>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("dish")}</th>
                      <th>{t("production")}</th>
                      <th>{uiLabel("prepMinutes", language)}</th>
                      <th>VND</th>
                      <th>{t("status")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {store.menu.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="dish-cell">
                            <span>{item.image}</span>
                            <div>
                              <b>{localize(item.name, language)}</b>
                              <small>
                                {localize(
                                  item.kitchenName,
                                  store.kitchenLanguages[0] ?? "vi",
                                )}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge tone="purple">
                            {valueLabel(item.station, language)}
                          </Badge>
                        </td>
                        <td>{item.prepMinutes}</td>
                        <td>
                          {formatMoney(
                            item.price,
                            store.baseCurrency,
                            language,
                          )}
                        </td>
                        <td>
                          <Badge tone={item.available ? "green" : "red"}>
                            {item.available ? t("available") : t("soldOut")}
                          </Badge>
                        </td>
                        <td>
                          <div className="row-actions">
                            <Button
                              variant="secondary"
                              className="small"
                              onClick={() => openMenuEditor(item)}
                            >
                              {t("edit")}
                            </Button>
                            <Button
                              variant="secondary"
                              className="small"
                              onClick={() => toggleMenu(item)}
                            >
                              {item.available ? t("soldOut") : t("available")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </>
          )}

          {page === "translations" && store && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("aiTitle")}</h1>
                  <p>{t("aiHint")}</p>
                </div>
                <Badge tone={translationConfig?.configured ? "green" : "amber"}>
                  {translationConfig?.configured
                    ? `${translationConfig.provider} · ${translationConfig.model}`
                    : t("aiNotConfigured")}
                </Badge>
              </div>
              <div className="dashboard-grid">
                <div className="stack">
                  <Panel title={t("generate")}>
                    <div className="panel-body translation-form">
                      <label className="field">
                        <span>{t("dish")}</span>
                        <select
                          className="select"
                          value={translationForm.itemId}
                          onChange={(event) =>
                            setTranslationForm((current) => ({
                              ...current,
                              itemId: event.target.value,
                            }))
                          }
                        >
                          <option value="">—</option>
                          {store.menu.map((item) => (
                            <option key={item.id} value={item.id}>
                              {localize(
                                item.name,
                                translationForm.sourceLocale,
                              )}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="field">
                        <span>{t("sourceLanguage")}</span>
                        <select
                          className="select"
                          value={translationForm.sourceLocale}
                          onChange={(event) =>
                            setTranslationForm((current) => ({
                              ...current,
                              sourceLocale: event.target.value as Locale,
                            }))
                          }
                        >
                          {supportedLocales.map((locale) => (
                            <option key={locale} value={locale}>
                              {localeLabels[locale]}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="field">
                        <span>{t("targetLanguages")}</span>
                        <div className="locale-checks">
                          {supportedLocales
                            .filter(
                              (locale) =>
                                locale !== translationForm.sourceLocale,
                            )
                            .map((locale) => (
                              <label key={locale}>
                                <input
                                  type="checkbox"
                                  checked={translationForm.targetLocales.includes(
                                    locale,
                                  )}
                                  onChange={(event) =>
                                    setTranslationForm((current) => ({
                                      ...current,
                                      targetLocales: event.target.checked
                                        ? [
                                            ...new Set([
                                              ...current.targetLocales,
                                              locale,
                                            ]),
                                          ]
                                        : current.targetLocales.filter(
                                            (item) => item !== locale,
                                          ),
                                    }))
                                  }
                                />{" "}
                                {localeLabels[locale]}
                              </label>
                            ))}
                        </div>
                      </div>
                      <Button
                        disabled={
                          !translationConfig?.configured ||
                          loading ||
                          !translationForm.itemId ||
                          !translationForm.targetLocales.length
                        }
                        onClick={generateTranslations}
                      >
                        {loading ? "…" : t("generate")}
                      </Button>
                    </div>
                  </Panel>
                  <Panel title={t("drafts")}>
                    <div className="panel-body stack">
                      {drafts.length === 0 ? (
                        <EmptyState
                          icon={<Languages />}
                          title={t("noDrafts")}
                        />
                      ) : (
                        drafts.map((draft) => (
                          <div className="draft-row" key={draft.id}>
                            <div>
                              <b>
                                {localize(
                                  store.menu.find(
                                    (item) => item.id === draft.menuItemId,
                                  )?.name ?? {},
                                  draft.sourceLocale,
                                )}{" "}
                                → {localeLabels[draft.targetLocale]}
                              </b>
                              <small>
                                {draft.fields.name} ·{" "}
                                {valueLabel(draft.status, language)}
                              </small>
                            </div>
                            <Badge
                              tone={
                                draft.status === "published" ? "green" : "amber"
                              }
                            >
                              {valueLabel(draft.status, language)}
                            </Badge>
                            {draft.status === "draft" && (
                              <Button
                                className="small"
                                onClick={() =>
                                  setDraftOpen(structuredClone(draft))
                                }
                              >
                                {t("reviewDraft")}
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </Panel>
                </div>
                <Panel title={t("glossary")}>
                  <form className="panel-body stack" onSubmit={addGlossary}>
                    <label className="field">
                      <span>{t("sourceLanguage")}</span>
                      <select
                        className="select"
                        value={glossaryForm.sourceLocale}
                        onChange={(event) =>
                          setGlossaryForm((current) => ({
                            ...current,
                            sourceLocale: event.target.value as Locale,
                          }))
                        }
                      >
                        {supportedLocales.map((locale) => (
                          <option key={locale} value={locale}>
                            {localeLabels[locale]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>{t("sourceTerm")}</span>
                      <input
                        className="input"
                        value={glossaryForm.sourceText}
                        onChange={(event) =>
                          setGlossaryForm((current) => ({
                            ...current,
                            sourceText: event.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <label className="field">
                      <span>{t("targetLanguages")}</span>
                      <select
                        className="select"
                        value={glossaryForm.targetLocale}
                        onChange={(event) =>
                          setGlossaryForm((current) => ({
                            ...current,
                            targetLocale: event.target.value as Locale,
                          }))
                        }
                      >
                        {supportedLocales.map((locale) => (
                          <option key={locale} value={locale}>
                            {localeLabels[locale]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>{t("preferredTerm")}</span>
                      <input
                        className="input"
                        value={glossaryForm.preferred}
                        onChange={(event) =>
                          setGlossaryForm((current) => ({
                            ...current,
                            preferred: event.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <Button>{t("addTerm")}</Button>
                    {glossary.map((entry) => (
                      <div className="split glossary-row" key={entry.id}>
                        <span>
                          <b>{entry.sourceText}</b>
                          <small>
                            {Object.entries(entry.translations)
                              .map(
                                ([locale, value]) =>
                                  `${localeLabels[locale as Locale] ?? locale}: ${value}`,
                              )
                              .join(" · ")}
                          </small>
                        </span>
                        <Badge>{localeLabels[entry.sourceLocale]}</Badge>
                      </div>
                    ))}
                  </form>
                </Panel>
              </div>
            </>
          )}

          {page === "staff" && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("teamTitle")}</h1>
                  <p>{t("teamHint")}</p>
                </div>
                <Button
                  onClick={() => {
                    setEmployeeForm((current) => ({
                      ...current,
                      storeIds: [storeId],
                    }));
                    setEmployeeOpen(true);
                  }}
                >
                  <Plus size={16} />
                  {t("addEmployee")}
                </Button>
              </div>
              <Panel>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("displayName")}</th>
                      <th>{t("username")}</th>
                      <th>{t("role")}</th>
                      <th>{t("language")}</th>
                      <th>{t("stores")}</th>
                      <th>{t("status")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((account) => (
                      <tr key={account.id}>
                        <td>
                          <b>{account.displayName}</b>
                        </td>
                        <td>{account.username}</td>
                        <td>
                          <select
                            className="select compact"
                            value={account.roles[0] ?? ""}
                            disabled={
                              account.roles.includes("owner") &&
                              !user.roles.includes("owner")
                            }
                            onChange={(event) =>
                              updateEmployee(account, {
                                roles: [event.target.value],
                              })
                            }
                          >
                            {merchantRoles.map((role) => (
                              <option key={role} value={role}>
                                {valueLabel(role, language)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>{localeLabels[account.preferredLanguage]}</td>
                        <td>
                          {account.storeIds
                            .map(
                              (id) =>
                                localize(
                                  stores.find((item) => item.id === id)?.name ??
                                    {},
                                  language,
                                ) ?? id,
                            )
                            .join(", ")}
                        </td>
                        <td>
                          <Badge tone={account.active ? "green" : "red"}>
                            {account.active ? t("enabled") : t("disabled")}
                          </Badge>
                        </td>
                        <td>
                          <div className="row-actions">
                            <Button
                              variant="secondary"
                              className="small"
                              onClick={() => resetEmployeePassword(account)}
                            >
                              {t("resetPassword")}
                            </Button>
                            {account.id !== user.id && (
                              <Button
                                variant={
                                  account.active ? "danger" : "secondary"
                                }
                                className="small"
                                onClick={() =>
                                  updateEmployee(account, {
                                    active: !account.active,
                                  })
                                }
                              >
                                {account.active ? t("disabled") : t("enabled")}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </>
          )}

          {page === "printers" && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("printers")}</h1>
                  <p>{t("kitchenLanguages")}</p>
                </div>
                <Badge tone="green">
                  <Wifi size={12} /> {uiLabel("online", language)}
                </Badge>
              </div>
              <div className="stats-grid">
                <StatCard
                  label={uiLabel("gateways", language)}
                  value="1 / 1"
                  icon={<Wifi />}
                />
                <StatCard label={t("printers")} value="4" icon={<Printer />} />
                <StatCard
                  label={uiLabel("pending", language)}
                  value={
                    printJobs.filter((job) => job.status === "pending").length
                  }
                  icon={<Clock3 />}
                />
                <StatCard
                  label={uiLabel("failed", language)}
                  value={
                    printJobs.filter((job) => job.status === "failed").length
                  }
                  icon={<XCircle />}
                />
              </div>
              <Panel>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{uiLabel("job", language)}</th>
                      <th>{uiLabel("order", language)}</th>
                      <th>{uiLabel("printer", language)}</th>
                      <th>{t("language")}</th>
                      <th>{t("status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printJobs.map((job) => (
                      <tr key={job.id}>
                        <td>{valueLabel(job.type, language)}</td>
                        <td>{job.orderId.slice(0, 8)}</td>
                        <td>{valueLabel(job.printerId, language)}</td>
                        <td>{localeListLabel(job.languages, language)}</td>
                        <td>
                          <Badge tone={orderTone(job.status)}>
                            {orderStatusLabel(job.status, language)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </>
          )}

          {page === "settings" && store && (
            <Panel title={operationsCopy[language].voice}>
              <div className="panel-body settings-grid">
                <label className="field">
                  <span>{operationsCopy[language].takeaway}</span>
                  <input
                    type="checkbox"
                    checked={storeSettings.queueTakeawayEnabled}
                    onChange={(event) =>
                      setStoreSettings((current) => ({
                        ...current,
                        queueTakeawayEnabled: event.target.checked,
                      }))
                    }
                  />
                </label>
                <label className="field">
                  <span>{operationsCopy[language].voice}</span>
                  <input
                    type="checkbox"
                    checked={storeSettings.queueVoice.enabled}
                    onChange={(event) =>
                      setStoreSettings((current) => ({
                        ...current,
                        queueVoice: {
                          ...current.queueVoice,
                          enabled: event.target.checked,
                        },
                      }))
                    }
                  />
                </label>
                <label className="field">
                  <span>{operationsCopy[language].repeats}</span>
                  <input
                    className="input"
                    type="number"
                    min="3"
                    max="10"
                    value={storeSettings.queueVoice.repeats}
                    onChange={(event) =>
                      setStoreSettings((current) => ({
                        ...current,
                        queueVoice: {
                          ...current.queueVoice,
                          repeats: Math.max(3, Number(event.target.value)),
                        },
                      }))
                    }
                  />
                </label>
                <label className="field">
                  <span>
                    {operationsCopy[language].interval} (
                    {operationsCopy[language].seconds})
                  </span>
                  <input
                    className="input"
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.1"
                    value={storeSettings.queueVoice.intervalMs / 1000}
                    onChange={(event) =>
                      setStoreSettings((current) => ({
                        ...current,
                        queueVoice: {
                          ...current.queueVoice,
                          intervalMs: Math.round(
                            Number(event.target.value) * 1000,
                          ),
                        },
                      }))
                    }
                  />
                </label>
              </div>
            </Panel>
          )}
          {page === "settings" && store && (
            <>
              <div className="content-head">
                <div>
                  <h1>{t("settings")}</h1>
                  <p>{localize(store.name, language)}</p>
                </div>
                <Button
                  disabled={
                    !storeSettings.languages.length ||
                    !storeSettings.kitchenLanguages.length
                  }
                  onClick={saveStoreSettings}
                >
                  {t("save")}
                </Button>
              </div>
              <div className="dashboard-grid">
                <div className="stack">
                  <Panel title={t("production")}>
                    <div className="panel-body settings-grid">
                      <label className="field">
                        <span>{uiLabel("storeType", language)}</span>
                        <input
                          className="input"
                          value={valueLabel(store.type, language)}
                          readOnly
                        />
                      </label>
                      <label className="field">
                        <span>{uiLabel("productionTrigger", language)}</span>
                        <select
                          className="select"
                          value={storeSettings.productionTrigger}
                          onChange={(event) =>
                            setStoreSettings((current) => ({
                              ...current,
                              productionTrigger: event.target
                                .value as Store["productionTrigger"],
                            }))
                          }
                        >
                          <option value="merchant_acceptance">
                            {valueLabel("merchant_acceptance", language)}
                          </option>
                          <option value="cashier_confirmation">
                            {valueLabel("cashier_confirmation", language)}
                          </option>
                          <option value="payment_webhook" disabled>
                            {valueLabel("payment_webhook", language)} ·{" "}
                            {valueLabel("reserved", language)}
                          </option>
                        </select>
                      </label>
                      <label className="field">
                        <span>{uiLabel("tableBinding", language)}</span>
                        <select
                          className="select"
                          value={storeSettings.tableBinding}
                          onChange={(event) =>
                            setStoreSettings((current) => ({
                              ...current,
                              tableBinding: event.target
                                .value as Store["tableBinding"],
                            }))
                          }
                        >
                          <option value="required">
                            {valueLabel("required", language)}
                          </option>
                          <option value="optional">
                            {valueLabel("optional", language)}
                          </option>
                          <option value="none">
                            {valueLabel("none", language)}
                          </option>
                        </select>
                      </label>
                      <label className="field">
                        <span>{uiLabel("currency", language)}</span>
                        <input
                          className="input"
                          value={store.baseCurrency}
                          readOnly
                        />
                      </label>
                    </div>
                  </Panel>
                  <Panel title={t("locationProtection")}>
                    <div className="panel-body">
                      <label className="field">
                        <span>
                          <MapPin size={14} /> {uiLabel("radius", language)}{" "}
                          (20–5000 m)
                        </span>
                        <input
                          className="input"
                          type="number"
                          min="20"
                          max="5000"
                          value={storeSettings.radiusMeters}
                          onChange={(event) =>
                            setStoreSettings((current) => ({
                              ...current,
                              radiusMeters: Number(event.target.value),
                            }))
                          }
                        />
                      </label>
                      <p className="muted tiny" style={{ marginTop: 10 }}>
                        {uiLabel("fallback", language)}
                      </p>
                    </div>
                  </Panel>
                  <Panel title={t("changePassword")}>
                    <form
                      className="panel-body settings-grid"
                      onSubmit={changePassword}
                    >
                      <label className="field">
                        <span>{t("currentPassword")}</span>
                        <input
                          className="input"
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(event) =>
                            setPasswordForm((current) => ({
                              ...current,
                              currentPassword: event.target.value,
                            }))
                          }
                          required
                        />
                      </label>
                      <label className="field">
                        <span>{t("newPassword")}</span>
                        <input
                          className="input"
                          type="password"
                          minLength={10}
                          value={passwordForm.newPassword}
                          onChange={(event) =>
                            setPasswordForm((current) => ({
                              ...current,
                              newPassword: event.target.value,
                            }))
                          }
                          required
                        />
                      </label>
                      <Button>{t("save")}</Button>
                    </form>
                  </Panel>
                </div>
                <div className="stack">
                  <Panel title={t("customerLanguages")}>
                    <div className="panel-body locale-checks">
                      {supportedLocales.map((locale) => (
                        <label key={locale}>
                          <input
                            type="checkbox"
                            checked={storeSettings.languages.includes(locale)}
                            onChange={(event) =>
                              setStoreSettings((current) => ({
                                ...current,
                                languages: event.target.checked
                                  ? [...current.languages, locale]
                                  : current.languages.filter(
                                      (item) => item !== locale,
                                    ),
                              }))
                            }
                          />{" "}
                          {localeLabels[locale]}
                        </label>
                      ))}
                    </div>
                  </Panel>
                  <Panel title={t("kitchenLanguages")}>
                    <div className="panel-body">
                      <div className="locale-checks">
                        {supportedLocales.map((locale) => (
                          <label key={locale}>
                            <input
                              type="checkbox"
                              checked={storeSettings.kitchenLanguages.includes(
                                locale,
                              )}
                              disabled={
                                !storeSettings.kitchenLanguages.includes(
                                  locale,
                                ) && storeSettings.kitchenLanguages.length >= 3
                              }
                              onChange={(event) =>
                                setStoreSettings((current) => ({
                                  ...current,
                                  kitchenLanguages: event.target.checked
                                    ? [...current.kitchenLanguages, locale]
                                    : current.kitchenLanguages.filter(
                                        (item) => item !== locale,
                                      ),
                                }))
                              }
                            />{" "}
                            {localeLabels[locale]}
                          </label>
                        ))}
                      </div>
                      <p className="muted tiny" style={{ margin: "10px 0 0" }}>
                        {uiLabel("maximumKitchenLanguages", language)}
                      </p>
                    </div>
                  </Panel>
                  <Panel title={t("paymentBoundary")}>
                    <div className="panel-body">
                      <Badge tone="green">
                        {uiLabel("platformFunds", language)}
                      </Badge>
                      <p className="muted tiny" style={{ marginTop: 12 }}>
                        {t("platformFundsZero")}
                      </p>
                    </div>
                  </Panel>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {menuOpen && store && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={saveMenuItem}>
            <div className="modal-header">
              <h2>
                {editingMenuId ? t("edit") : t("create")} {t("dish")}
              </h2>
              <button
                className="icon-button"
                type="button"
                onClick={() => setMenuOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content stack">
              <div className="settings-grid">
                <label className="field">
                  <span>{t("sourceLanguage")}</span>
                  <select
                    className="select"
                    value={menuForm.sourceLocale}
                    onChange={(event) =>
                      setMenuForm((current) => ({
                        ...current,
                        sourceLocale: event.target.value as Locale,
                      }))
                    }
                  >
                    {supportedLocales.map((locale) => (
                      <option key={locale} value={locale}>
                        {localeLabels[locale]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{uiLabel("category", language)}</span>
                  <select
                    className="select"
                    value={menuForm.categoryId}
                    onChange={(event) =>
                      setMenuForm((current) => ({
                        ...current,
                        categoryId: event.target.value,
                      }))
                    }
                  >
                    {store.categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {localize(category.name, language)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="field">
                <span>{t("dish")}</span>
                <input
                  className="input"
                  value={menuForm.name}
                  onChange={(event) =>
                    setMenuForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("kitchenName")}</span>
                <input
                  className="input"
                  value={menuForm.kitchenName}
                  onChange={(event) =>
                    setMenuForm((current) => ({
                      ...current,
                      kitchenName: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("description")}</span>
                <textarea
                  className="textarea"
                  value={menuForm.description}
                  onChange={(event) =>
                    setMenuForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </label>
              <div className="settings-grid">
                <label className="field">
                  <span>
                    {uiLabel("price", language)} ({store.baseCurrency})
                  </span>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    step="1"
                    value={menuForm.price}
                    onChange={(event) =>
                      setMenuForm((current) => ({
                        ...current,
                        price: Number(event.target.value),
                      }))
                    }
                    required
                  />
                </label>
                <label className="field">
                  <span>{uiLabel("station", language)}</span>
                  <select
                    className="select"
                    value={menuForm.station}
                    onChange={(event) =>
                      setMenuForm((current) => ({
                        ...current,
                        station: event.target.value as MenuItem["station"],
                      }))
                    }
                  >
                    {["hot", "cold", "bar", "dessert"].map((station) => (
                      <option key={station} value={station}>
                        {valueLabel(station, language)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{uiLabel("prepMinutes", language)}</span>
                  <input
                    className="input"
                    type="number"
                    min="1"
                    max="240"
                    value={menuForm.prepMinutes}
                    onChange={(event) =>
                      setMenuForm((current) => ({
                        ...current,
                        prepMinutes: Number(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="field">
                  <span>{uiLabel("image", language)}</span>
                  <input
                    className="input"
                    value={menuForm.image}
                    onChange={(event) =>
                      setMenuForm((current) => ({
                        ...current,
                        image: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <label className="field">
                <span>{uiLabel("allergens", language)}</span>
                <input
                  className="input"
                  value={menuForm.allergens}
                  onChange={(event) =>
                    setMenuForm((current) => ({
                      ...current,
                      allergens: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setMenuOpen(false)}
              >
                {t("cancel")}
              </Button>
              <Button disabled={loading}>{t("save")}</Button>
            </div>
          </form>
        </div>
      )}

      {employeeOpen && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={createEmployee}>
            <div className="modal-header">
              <h2>{t("addEmployee")}</h2>
              <button
                className="icon-button"
                type="button"
                onClick={() => setEmployeeOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content stack">
              <label className="field">
                <span>{t("displayName")}</span>
                <input
                  className="input"
                  value={employeeForm.displayName}
                  onChange={(event) =>
                    setEmployeeForm((current) => ({
                      ...current,
                      displayName: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("username")}</span>
                <input
                  className="input"
                  pattern="[a-zA-Z0-9._-]+"
                  value={employeeForm.username}
                  onChange={(event) =>
                    setEmployeeForm((current) => ({
                      ...current,
                      username: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("temporaryPassword")}</span>
                <input
                  className="input"
                  type="password"
                  minLength={10}
                  value={employeeForm.password}
                  onChange={(event) =>
                    setEmployeeForm((current) => ({
                      ...current,
                      password: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("role")}</span>
                <select
                  className="select"
                  value={employeeForm.role}
                  onChange={(event) =>
                    setEmployeeForm((current) => ({
                      ...current,
                      role: event.target.value as MerchantRole,
                    }))
                  }
                >
                  {merchantRoles
                    .filter(
                      (role) =>
                        user.roles.includes("owner") || role !== "owner",
                    )
                    .map((role) => (
                      <option key={role} value={role}>
                        {valueLabel(role, language)}
                      </option>
                    ))}
                </select>
              </label>
              <label className="field">
                <span>{t("language")}</span>
                <select
                  className="select"
                  value={employeeForm.preferredLanguage}
                  onChange={(event) =>
                    setEmployeeForm((current) => ({
                      ...current,
                      preferredLanguage: event.target.value as Locale,
                    }))
                  }
                >
                  {supportedLocales.map((locale) => (
                    <option key={locale} value={locale}>
                      {localeLabels[locale]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="field">
                <span>{t("stores")}</span>
                {stores.map((item) => (
                  <label key={item.id}>
                    <input
                      type="checkbox"
                      checked={employeeForm.storeIds.includes(item.id)}
                      onChange={(event) =>
                        setEmployeeForm((current) => ({
                          ...current,
                          storeIds: event.target.checked
                            ? [...current.storeIds, item.id]
                            : current.storeIds.filter((id) => id !== item.id),
                        }))
                      }
                    />{" "}
                    {localize(item.name, language)}
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEmployeeOpen(false)}
              >
                {t("cancel")}
              </Button>
              <Button disabled={loading}>{t("create")}</Button>
            </div>
          </form>
        </div>
      )}

      {draftOpen && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <div>
                <h2>{t("reviewDraft")}</h2>
                <p className="muted tiny">
                  {localeLabels[draftOpen.sourceLocale]} →{" "}
                  {localeLabels[draftOpen.targetLocale]}
                </p>
              </div>
              <button
                className="icon-button"
                onClick={() => setDraftOpen(undefined)}
              >
                ×
              </button>
            </div>
            <div className="modal-content stack">
              <label className="field">
                <span>{t("dish")}</span>
                <input
                  className="input"
                  value={draftOpen.fields.name}
                  onChange={(event) =>
                    setDraftOpen({
                      ...draftOpen,
                      fields: { ...draftOpen.fields, name: event.target.value },
                    })
                  }
                />
              </label>
              <label className="field">
                <span>{t("kitchenName")}</span>
                <input
                  className="input"
                  value={draftOpen.fields.kitchenName}
                  onChange={(event) =>
                    setDraftOpen({
                      ...draftOpen,
                      fields: {
                        ...draftOpen.fields,
                        kitchenName: event.target.value,
                      },
                    })
                  }
                />
              </label>
              <label className="field">
                <span>{t("description")}</span>
                <textarea
                  className="textarea"
                  value={draftOpen.fields.description}
                  onChange={(event) =>
                    setDraftOpen({
                      ...draftOpen,
                      fields: {
                        ...draftOpen.fields,
                        description: event.target.value,
                      },
                    })
                  }
                />
              </label>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => saveDraft(draftOpen)}>
                {t("save")}
              </Button>
              <Button onClick={() => publishDraft(draftOpen)}>
                {t("publish")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {temporaryPassword && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2>{t("temporaryPassword")}</h2>
            </div>
            <div className="modal-content">
              <p className="muted tiny">{t("temporaryPassword")}</p>
              <div className="secret-value">{temporaryPassword}</div>
            </div>
            <div className="modal-footer">
              <Button onClick={() => setTemporaryPassword("")}>
                {t("close")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {tableOpen && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={createTable}>
            <div className="modal-header">
              <h2>{t("tables")}</h2>
              <button
                className="icon-button"
                type="button"
                onClick={() => setTableOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content stack">
              <label className="field">
                <span>{t("tables")}</span>
                <input
                  className="input"
                  value={tableForm.name}
                  onChange={(e) =>
                    setTableForm({ ...tableForm, name: e.target.value })
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{uiLabel("seats", language)}</span>
                <input
                  className="input"
                  type="number"
                  min="1"
                  max="50"
                  value={tableForm.seats}
                  onChange={(e) =>
                    setTableForm({
                      ...tableForm,
                      seats: Number(e.target.value),
                    })
                  }
                />
              </label>
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setTableOpen(false)}
              >
                {t("cancel")}
              </Button>
              <Button disabled={loading}>{t("create")}</Button>
            </div>
          </form>
        </div>
      )}
      {selectedTableId &&
        store &&
        (() => {
          const table = store.tables.find(
            (candidate) => candidate.id === selectedTableId,
          );
          const order = orders.find(
            (candidate) =>
              candidate.tableId === selectedTableId &&
              !["completed", "cancelled"].includes(candidate.status),
          );
          if (!table) return null;
          return (
            <div className="modal-backdrop">
              <div className="modal">
                <div className="modal-header">
                  <h2>
                    {operationsCopy[language].details} · {table.name}
                  </h2>
                  <button
                    className="icon-button"
                    onClick={() => setSelectedTableId("")}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-content stack">
                  {order ? (
                    <>
                      <div>
                        <b>{order.displayNumber}</b> ·{" "}
                        <Badge tone={orderTone(order.status)}>
                          {orderStatusLabel(order.status, language)}
                        </Badge>
                      </div>
                      <h3>{operationsCopy[language].dishes}</h3>
                      {order.items.map((item) => (
                        <div className="split" key={item.orderItemId}>
                          <span>
                            {item.quantity}×{" "}
                            {localize(item.nameSnapshot, language)}
                          </span>
                          <Badge tone={orderTone(item.status)}>
                            {orderStatusLabel(item.status, language)}
                          </Badge>
                        </div>
                      ))}
                      <label className="field">
                        <span>{operationsCopy[language].move}</span>
                        <select
                          className="select"
                          defaultValue=""
                          onChange={(event) =>
                            void moveTable(order, event.target.value)
                          }
                        >
                          <option value="" disabled>
                            {operationsCopy[language].move}
                          </option>
                          {store.tables
                            .filter(
                              (candidate) =>
                                candidate.active &&
                                (candidate.status ?? "available") ===
                                  "available",
                            )
                            .map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.name} · {candidate.seats}
                              </option>
                            ))}
                        </select>
                      </label>
                    </>
                  ) : (
                    <EmptyState
                      icon={<Table2 />}
                      title={
                        tableStateCopy[language][table.status ?? "available"]
                      }
                    />
                  )}
                </div>
                <div className="modal-footer">
                  <Button onClick={() => setSelectedTableId("")}>
                    {t("close")}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      {qrPreview && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>{qrPreview.name} · QR</h2>
              <button
                className="icon-button"
                onClick={() => setQrPreview(undefined)}
              >
                ×
              </button>
            </div>
            <div className="modal-content" style={{ textAlign: "center" }}>
              <img
                src={qrPreview.image}
                alt={`QR ${qrPreview.name}`}
                style={{ maxWidth: "100%" }}
              />
              <p className="tiny muted" style={{ wordBreak: "break-all" }}>
                {qrPreview.url}
              </p>
            </div>
            <div className="modal-footer">
              <a
                href={qrPreview.image}
                download={`table-${qrPreview.name}-qr.png`}
              >
                {uiLabel("download", language)}
              </a>
              <Button onClick={() => setQrPreview(undefined)}>
                {t("close")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
